import {
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { KeyedMutator } from 'swr/_internal';
import { Add, Delete } from '@mui/icons-material';
import uuid from 'react-uuid';
import { AccountData, AccountService } from '../api';
import { FormTextField } from '../experienceSettings/formComponents/FormTextField';
import { dynamicLabelSchema } from '../experienceSettings/sections/informationSection/dynamicLabelSchema';
import { useLanguage } from '../common/GeneralUtils';
import { WorkshopCircle } from '../common/ui/WorkshopCircle';

type AccountSharedResourcesSettingsProps = {
  account: AccountData;
  updateLocalAccount: KeyedMutator<AccountData>;
  setIsUpdatedSnackOpen: (value: boolean) => void;
};

export const AccountSharedResourcesSettings = ({
  account,
  updateLocalAccount,
  setIsUpdatedSnackOpen,
}: AccountSharedResourcesSettingsProps) => {
  const language = useLanguage();
  const { t } = useTranslation();
  const [isUpdatingSettings, setIsUpdatingSettings] = React.useState(false);

  const initialValues = {
    automatic_dividable_resources: account.shared_resources?.automatic_dividable_resources,
  };

  const validationSchema = yup.object().shape({
    automatic_dividable_resources: yup.array().of(
      yup.object().shape({
        id: yup.string(),
        label: yup.object().shape(dynamicLabelSchema(t, account.customer_facing_widgets_languages, true)),
        unit_capacity: yup.number().min(1),
        total_units: yup.number().min(1),
      }),
    ),
  });

  const onSubmit = async (values: typeof initialValues) => {
    console.log('Submitting shared resources settings:', values);
    setIsUpdatingSettings(true);
    try {
      if (values) {
        const updatedAccount = await AccountService.sharedResources(values);
        await updateLocalAccount(updatedAccount);
      }
      setIsUpdatedSnackOpen(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsUpdatingSettings(false);
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ handleSubmit, values, handleChange }) => {
        return (
          <Form onSubmit={handleSubmit}>
            <Grid container p={0} flexWrap="nowrap" alignItems="flex-start">
              <Grid container flexDirection="column">
                <Grid item mb={2}>
                  <Typography variant="h4">{t('sharedResources.title')}</Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" color="grey" mb={2}>
                    {t('sharedResources.description')}
                  </Typography>
                </Grid>
                <Grid item>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('sharedResources.name')}</TableCell>
                          <TableCell>{t('sharedResources.totalUnits')}</TableCell>
                          <TableCell>{t('sharedResources.unitCapacity')}</TableCell>
                          <TableCell>{t('sharedResources.allocatedTo')}</TableCell>

                          <TableCell width="40px">{t('sharedResources.actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {values.automatic_dividable_resources?.map((resource, index) => {
                          const resourceAllocationsOrderPageIds = account.experiences[0].order_pages
                            ?.map((orderPage) => {
                              const hasAllocation =
                                orderPage.shared_resources_allocations?.automatic_dividable_resources?.some(
                                  (allocation) => allocation.resource_id === resource.id,
                                );
                              return hasAllocation ? orderPage.id : null;
                            })
                            .filter(Boolean) as string[];

                          return (
                            <TableRow key={resource.id}>
                              <TableCell>
                                <Grid container gap={1}>
                                  {account.customer_facing_widgets_languages.map((lang) => (
                                    <Grid item>
                                      <FormTextField
                                        width={100}
                                        label={t(`emailTemplates.${lang}`)}
                                        fieldName={`automatic_dividable_resources.${index}.label.${lang}`}
                                        isFieldUpdate
                                        disabled={false}
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              </TableCell>
                              <TableCell>
                                <FormTextField
                                  width={50}
                                  type="number"
                                  fieldName={`automatic_dividable_resources.${index}.total_units`}
                                  disabled={false}
                                  isFieldUpdate
                                />
                              </TableCell>
                              <TableCell>
                                <FormTextField
                                  width={50}
                                  type="number"
                                  fieldName={`automatic_dividable_resources.${index}.unit_capacity`}
                                  disabled={false}
                                  isFieldUpdate
                                />
                              </TableCell>

                              <TableCell>
                                <Grid container gap={1}>
                                  {resourceAllocationsOrderPageIds.map((orderPageId) => (
                                    <Grid item key={orderPageId}>
                                      <WorkshopCircle
                                        account={account}
                                        orderPageId={orderPageId}
                                        withTooltip
                                        language={language}
                                      />
                                    </Grid>
                                  ))}
                                </Grid>
                              </TableCell>
                              <TableCell width="40px" style={{ padding: 0 }}>
                                <Tooltip
                                  title={
                                    resourceAllocationsOrderPageIds.length > 0
                                      ? t('sharedResources.deleteResourceTooltip')
                                      : undefined
                                  }>
                                  <span>
                                    <IconButton
                                      onClick={() => {
                                        handleChange({
                                          target: {
                                            name: 'automatic_dividable_resources',
                                            value: values.automatic_dividable_resources?.filter((_, i) => i !== index),
                                          },
                                        });
                                      }}
                                      disabled={isUpdatingSettings || resourceAllocationsOrderPageIds.length > 0}>
                                      <Delete />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <Button
                      startIcon={<Add />}
                      onClick={() => {
                        handleChange({
                          target: {
                            name: 'automatic_dividable_resources',
                            value: [
                              ...(values.automatic_dividable_resources || []),
                              {
                                id: uuid(),
                                label: { he: '' },
                                unit_capacity: 1,
                                total_units: 1,
                              },
                            ],
                          },
                        });
                      }}
                      disabled={isUpdatingSettings}
                      sx={{ m: 1 }}>
                      {t('sharedResources.addResource')}
                    </Button>
                  </TableContainer>
                </Grid>
              </Grid>
              <Grid container justifyContent="flex-end" mt={2} xs={2}>
                <Button type="submit" variant="contained" disabled={isUpdatingSettings}>
                  {t('save')}
                </Button>
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};
